import apiClient from "../lib/api-client";

function mutationHeaders() {
  return {
    headers: {
      "Idempotency-Key": crypto.randomUUID(),
      // Approval mode can add: "X-Change-Reason": reason.trim()
    },
  };
}

export type SupplierType =
  | "DISTRIBUTOR"
  | "MANUFACTURER"
  | "WHOLESALER"
  | "IMPORTER"
  | "STOCKIST";

export interface Supplier {
  id: string;
  tenantId: string;
  name: string;
  code?: string;
  phone: string;
  email?: string;
  contactPerson?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstNumber?: string;
  panNumber?: string;
  drugLicenseNo?: string;
  drugLicenseExpiry?: string;
  supplierType: SupplierType;
  deliveryLeadDays: number;
  minimumOrderValue: number;
  rating: number;
  returnPolicy?: string;
  creditDays: number;
  creditLimit: number;
  currentBalance: number;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  tdsApplicable: boolean;
  tdsPercent: number;
  tdsSection?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    purchaseOrders: number;
    goodsReceipts: number;
    payments: number;
  };
}

export interface CreateSupplierDto {
  name: string;
  code?: string;
  phone: string;
  email?: string;
  contactPerson?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstNumber?: string;
  panNumber?: string;
  drugLicenseNo?: string;
  drugLicenseExpiry?: string;
  supplierType?: SupplierType;
  deliveryLeadDays?: number;
  minimumOrderValue?: number;
  rating?: number;
  returnPolicy?: string;
  creditDays?: number;
  creditLimit?: number;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  tdsApplicable?: boolean;
  tdsPercent?: number;
  tdsSection?: string;
  isActive?: boolean;
}

export interface UpdateSupplierDto extends Partial<CreateSupplierDto> {}

export interface SupplierChangeRequest {
  id: string;
  operation:
    | "CREATE"
    | "UPDATE"
    | "INACTIVATE"
    | "RESTORE"
    | "IMPORT"
    | "ROLLBACK";
  status: "PENDING" | "APPROVED" | "REJECTED";
  proposedDiff: Record<string, unknown>;
  validationResult: Record<string, unknown>;
  reason: string;
  requestedBy: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewReason?: string;
  createdAt: string;
  record: {
    id: string;
    kind: string;
    naturalKey: string;
    displayName: string;
    lifecycle: string;
  };
  version: {
    id: string;
    version: number;
    status: string;
    effectiveFrom: string;
    payload: Record<string, unknown>;
  };
}

export interface SupplierMutationResponse {
  authorityStatus: "PENDING_APPROVAL";
  operationalEntityType: "SUPPLIER";
  operationalEntityId?: string;
  request: SupplierChangeRequest;
}

const optionalTextFields = [
  "code",
  "email",
  "contactPerson",
  "address",
  "city",
  "state",
  "pincode",
  "gstNumber",
  "panNumber",
  "drugLicenseNo",
  "drugLicenseExpiry",
  "returnPolicy",
  "bankName",
  "accountNumber",
  "ifscCode",
  "tdsSection",
] as const;

function normalizeSupplierPayload<
  T extends CreateSupplierDto | UpdateSupplierDto,
>(data: T, clearOptionalBlanks = false): T {
  const payload = { ...data } as Record<string, unknown>;
  if (typeof payload.name === "string") payload.name = payload.name.trim();
  if (typeof payload.phone === "string") {
    payload.phone = payload.phone.trim().replace(/[\s()-]/g, "");
  }
  for (const field of optionalTextFields) {
    const value = payload[field];
    if (typeof value !== "string") continue;
    const normalized = value.trim();
    if (!normalized) {
      if (clearOptionalBlanks) payload[field] = null;
      else delete payload[field];
      continue;
    }
    payload[field] =
      field === "email"
        ? normalized.toLowerCase()
        : [
              "code",
              "gstNumber",
              "panNumber",
              "drugLicenseNo",
              "ifscCode",
              "tdsSection",
            ].includes(field)
          ? normalized.toUpperCase()
          : normalized;
  }
  return payload as T;
}

export interface SupplierPaymentDto {
  supplierId: string;
  amount: number;
  paymentMethod: "CASH" | "CARD" | "UPI" | "CHEQUE" | "BANK_TRANSFER";
  paymentDate?: string;
  allocatedGRNs?: { grnId: string; amount: number }[];
  reference?: string;
  notes?: string;
}

export interface LedgerEntry {
  id: string;
  date: string;
  type: "GRN" | "PAYMENT" | "RETURN";
  reference: string;
  description: string;
  debit: number | null;
  credit: number | null;
}

export const supplierService = {
  async getSuppliers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<{
    data: Supplier[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get("/suppliers", { params });
    return response.data;
  },

  async getSupplier(id: string): Promise<Supplier> {
    const response = await apiClient.get(`/suppliers/${id}`);
    return response.data;
  },

  async createSupplier(data: CreateSupplierDto): Promise<Supplier> {
    const response = await apiClient.post(
      "/suppliers",
      normalizeSupplierPayload(data),
      mutationHeaders(),
    );
    return response.data;
  },

  async updateSupplier(id: string, data: UpdateSupplierDto): Promise<Supplier> {
    const response = await apiClient.patch(
      `/suppliers/${id}`,
      normalizeSupplierPayload(data, true),
      mutationHeaders(),
    );
    return response.data;
  },

  async deleteSupplier(id: string): Promise<Supplier> {
    const response = await apiClient.delete(
      `/suppliers/${id}`,
      mutationHeaders(),
    );
    return response.data;
  },

  // Approval-mode API is retained for a possible future deployment. The
  // pharmacy UI does not route to or call these methods.
  async getSupplierChanges(params?: {
    status?: SupplierChangeRequest["status"];
    page?: number;
    limit?: number;
  }): Promise<{
    data: SupplierChangeRequest[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const response = await apiClient.get("/master-data/changes", {
      params: {
        kind: "SUPPLIER_IDENTITY",
        status: "PENDING",
        ...params,
      },
    });
    return response.data;
  },

  async approveSupplierChange(requestId: string, reason: string) {
    const response = await apiClient.post(
      `/master-data/changes/${requestId}/approve`,
      { reason: reason.trim() },
      mutationHeaders(),
    );
    return response.data as SupplierChangeRequest;
  },

  async rejectSupplierChange(requestId: string, reason: string) {
    const response = await apiClient.post(
      `/master-data/changes/${requestId}/reject`,
      { reason: reason.trim() },
      mutationHeaders(),
    );
    return response.data as SupplierChangeRequest;
  },

  async getSupplierSummary(id: string): Promise<{
    supplier: {
      id: string;
      name: string;
      currentBalance: number;
      creditLimit: number;
      creditDays: number;
    };
    stats: {
      totalPurchases: number;
      totalPaid: number;
      outstandingBalance: number;
      grnCount: number;
      paymentCount: number;
      pendingPOs: number;
    };
  }> {
    const response = await apiClient.get(`/suppliers/${id}/summary`);
    return response.data;
  },

  async getSupplierLedger(
    id: string,
    params?: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<{
    data: LedgerEntry[];
    summary: {
      totalDebit: number;
      totalCredit: number;
      balance: number;
      grnCount: number;
      paymentCount: number;
    };
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get(`/suppliers/${id}/ledger`, { params });
    return response.data;
  },

  async recordPayment(data: SupplierPaymentDto): Promise<any> {
    const response = await apiClient.post(
      `/suppliers/${data.supplierId}/payments`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async getSupplierPayments(
    supplierId: string,
    params?: { page?: number; limit?: number },
  ): Promise<{
    data: any[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get(`/suppliers/${supplierId}/payments`, {
      params,
    });
    return response.data;
  },

  async getOutstandingSuppliers(): Promise<{
    suppliers: Supplier[];
    totalOutstanding: number;
    supplierCount: number;
  }> {
    const response = await apiClient.get("/suppliers/outstanding");
    return response.data;
  },
};
